import { useState, useEffect } from "react";
import {
  BookTemplate, Copy, CheckCircle, AlertCircle, Dumbbell,
  Calendar, Users, ChevronRight, Download
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const NIVEAU_KLEUR = {
  Beginner: "text-green-400 bg-green-500/10 border-green-500/20",
  Gemiddeld: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  Gevorderd: "text-red-400 bg-red-500/10 border-red-500/20",
};

export default function TemplatesTab({ token }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(null);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetch(`${API}/api/company/assigned-templates`, auth)
      .then(r => r.json())
      .then(d => { setTemplates(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleCopy = async (templateId, naam) => {
    setCopying(templateId);
    setResult(null);
    try {
      const r = await fetch(`${API}/api/company/assigned-templates/${templateId}/copy`, {
        method: "POST",
        headers: auth.headers,
      });
      const d = await r.json();
      if (r.ok) {
        setResult({ ok: true, msg: `"${naam}" is gekopieerd naar jouw schema's!` });
      } else {
        setResult({ ok: false, msg: d.detail || "Kopiëren mislukt" });
      }
    } catch {
      setResult({ ok: false, msg: "Netwerk fout. Probeer opnieuw." });
    }
    setCopying(null);
    setTimeout(() => setResult(null), 5000);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-5">
      <div>
        <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Bibliotheek</p>
        <h2 className="text-xl font-bold text-white">Schema Templates</h2>
        <p className="text-zinc-500 text-sm mt-1">
          Door FitSmith toegewezen schema templates. Kopieer ze naar jouw eigen schema-collectie.
        </p>
      </div>

      {result && (
        <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${result.ok ? "bg-green-500/10 border border-green-500/20 text-green-400" : "bg-red-500/10 border border-red-500/20 text-red-400"}`}>
          {result.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
          {result.msg}
        </div>
      )}

      {templates.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-[#1a1a1a] border border-white/6 flex items-center justify-center mx-auto mb-4">
            <Dumbbell size={24} className="text-zinc-600" />
          </div>
          <h3 className="text-white font-semibold mb-2">Nog geen templates toegewezen</h3>
          <p className="text-zinc-500 text-sm max-w-xs mx-auto">
            FitSmith kan schema templates toewijzen aan jouw account. Neem contact op via info@fitsmith.nl.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(t => (
            <div
              key={t.id}
              className="bg-[#111] border border-white/8 rounded-2xl p-5 hover:border-[#C9A84C]/30 transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center">
                  <Dumbbell size={16} className="text-[#C9A84C]" />
                </div>
                {t.niveau && (
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${NIVEAU_KLEUR[t.niveau] || NIVEAU_KLEUR.Beginner}`}>
                    {t.niveau}
                  </span>
                )}
              </div>

              <h3 className="text-white font-bold mb-1 leading-snug">{t.naam}</h3>
              {t.beschrijving && (
                <p className="text-zinc-500 text-xs leading-relaxed mb-3 line-clamp-2">{t.beschrijving}</p>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
                  <Calendar size={11} />
                  <span>{t.trainingsdagen} dagen/week</span>
                </div>
                <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
                  <Users size={11} />
                  <span>{t.weken} weken</span>
                </div>
              </div>

              <button
                onClick={() => handleCopy(t.id, t.naam)}
                disabled={copying === t.id}
                data-testid={`copy-template-${t.id}`}
                className="w-full py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm font-bold transition-all bg-[#C9A84C]/10 border border-[#C9A84C]/25 text-[#C9A84C] hover:bg-[#C9A84C]/20 disabled:opacity-50"
              >
                {copying === t.id ? (
                  <div className="w-3.5 h-3.5 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
                ) : (
                  <Download size={13} />
                )}
                {copying === t.id ? "Kopiëren..." : "Naar mijn schema's"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
